<template>
  <div>
    <naj-box :title="local.caseRelatedRequest.VerdictObjectionRequest.title">
      <v-row>
        <v-col cols="3">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="formModel.judgementCode"
            :label="local.caseRelatedRequest.VerdictObjectionRequest.judgementCode"
            :items="judgments"
            item-value="judgementCode"
            item-text="judgementCode"
            :validates="`required`"
            :disabled="isViewMode || isEditMode"
            @change="setSelectedJudgment"
          ></naj-input>
        </v-col>

        <v-col
          cols="1"
          class="judgement-deed"
        >
          <naj-button
            :disabled="!selectedJudgment.objectKey"
            @click="getJudgementDeed"
          >
            {{ local.caseRelatedRequest.VerdictObjectionRequest.displayJudgement }}
          </naj-button>
        </v-col>

        <v-col cols="4">
          <naj-input
            v-if="!!formModel.appealType"
            :type-id="formFieldType.selectbox"
            :value.sync="formModel.appealType.Value"
            :value-name.sync="formModel.appealType.Text"
            :label="local.caseRelatedRequest.VerdictObjectionRequest.appealType"
            :items="validAppealTypes"
            item-value="id"
            item-text="name"
            validates="required"
            :disabled="isViewMode || isAudit || disabledAppealType"
          ></naj-input>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <naj-input
            :type-id="formFieldType.textarea"
            :label="local.caseRelatedRequest.VerdictObjectionRequest.objectionJudgment"
            :value.sync="selectedJudgment.judgementText"
            disabled
            validates="max:1000000"
          ></naj-input>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="memorandum-challenge-alert-container my-4">
            <span class="message">
              <v-icon color="#f6c645">
                mdi-alert
              </v-icon>
              {{ local.caseRelatedRequest.VerdictObjectionRequest.memorandumChallengeAlert }}
            </span>
          </div>

          <naj-input
            :type-id="formFieldType.textarea"
            :label="local.caseRelatedRequest.VerdictObjectionRequest.memorandumChallenge"
            :value.sync="formModel.opposistionText"
            validates="required|max:10000|allowed_special_character"
            :disabled="isViewMode"
          ></naj-input>
        </v-col>
      </v-row>

      <v-row>
        <naj-alert type="warning">
          <template slot="content">
            {{ local.caseRelatedRequest.VerdictObjectionRequest.attachmentNote }}
          </template>
        </naj-alert>
      </v-row>
      <v-row>
        <v-col
          v-if="formModel.judgementFile && formModel.judgementFile.length != 0"
          cols="3"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.caseRelatedRequest.VerdictObjectionRequest.objectionNoteAttachment }}
          </p>
          <attachment-card
            :file="{ ...uploadedFile }"
            :show-delete-button="!isViewMode"
            @deleteAttachment="showDeleteObjectionNoteModal"
            @viewAttachment="viewAttachment"
          ></attachment-card>
        </v-col>
        <v-col
          v-else
          class="objection-note-attachment"
          cols="12"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.caseRelatedRequest.VerdictObjectionRequest.addObjectionNoteAttachment }}
          </p>
          <load-local-attachment
            :max-file-size="5"
            :upload-disabled="isViewMode"
            @fileSelected="fileSelected"
          >
          </load-local-attachment>
        </v-col>
      </v-row>
    </naj-box>

    <case-judgement-modal
      :selected-judgement="selectedJudgment"
      :modal-visibility="showCaseJudgementModal"
      @modalClose="closeCaseJudgementModal"
    ></case-judgement-modal>

    <render-attachment-modal
      :attachment-file="uploadedFile"
      :modal-visibility="showViewAttachmentModal"
      @modalClose="hideViewAttachmentModal"
    >
    </render-attachment-modal>

    <naj-confirmation
      :show-modal="showDeleteObjectionNoteAttachment"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.caseRelatedRequest.VerdictObjectionRequest.removeObjectionNoteAttachmentMessage"
      :ok-btn-label="local.common.remove"
      @ok="confirmDeleteObjectionNote"
      @cancel="cancelDeleteObjectionNote"
    ></naj-confirmation>
    <naj-alert-modal
      :show-modal="showAlertModal"
      type="information"
      :title="local.caseFile.secretJudgementsInfoTitle"
      :message="local.caseFile.secretJudgementsInfo"
      @onCancelModal="showAlertModal=false"
    >
    </naj-alert-modal>
    <naj-alert-modal
      :show-modal="showAlertModalForCriminalCases"
      type="information"
      :title="local.common.criminalAttachmentTitle"
      :message="local.common.criminalAttachment"
      @onCancelModal="showAlertModalForCriminalCases=false"
    >
    </naj-alert-modal>
  </div>
</template>

<script>
import { mode, socialIdentityTypeEnum, countriesEnum } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState } = createNamespacedHelpers('LookupsModule');
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapActions } = createNamespacedHelpers('CasesModule');
import CaseJudgementModal from '../../../../cases/case-judgement-modal';
import LoadLocalAttachment from '../../../../../components/shared/attachment-modal/load-local-attachment';
import AttachmentCard from '@/components/shared/attachment-modal/attachment-card';
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';

export default {
  name: 'VerdictObjectionRequest',
  inject: ['$validator'],
  components: {
    CaseJudgementModal,
    LoadLocalAttachment,
    AttachmentCard,
    RenderAttachmentModal,
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
      requestSubjects: [],
      declaration: false,
    },
    requiredData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      judgments: [],
      showCaseJudgementModal: false,
      showDeleteObjectionNoteAttachment: false,
      showViewAttachmentModal: false,
      showAlertModal: false,
      showAlertModalForCriminalCases: false,
      selectedJudgment: {},
      validAppealTypes: [],
      currentAppealType: 0,
    };
  },
  computed: {
    ...mapLUTState(['appealType']),
    ...mapGlobalState(['userInfo']),
    isViewMode() {
      return this.formModel && this.formModel.mode === mode.view;
    },
    isEditMode() {
      return this.formModel && this.formModel.mode === mode.update;
    },
    isNewMode() {
      return this.$route.params.requestObjectKey === 'new';
    },
    uploadedFile() {
      if (this.formModel.judgementFile && this.formModel.judgementFile.length != 0) {
        return {
          name: this.formModel.judgementFile[0].fileData.fileName,
          attachmentKey: this.formModel.judgementFile[0].fileData.attachmentKey,
          sharePointId: this.formModel.judgementFile[0].fileData.attachement,
          isImage: this.formModel.judgementFile[0].fileData.fileType.startsWith('image'),
          fileType: this.formModel.judgementFile[0].fileData.fileType,
        };
      }
      return {};
    },
    disabledAppealType() {
      return this.validAppealTypes.length == 1;
    },
    isAudit() {
      return this.isEditMode && this.currentAppealType === this.appealType[1].id;
    },
    routeRequestObjectKey() {
      return this.$route.query.requestObjectKey;
    },
  },
  watch: {},
  mounted() {

    if (this.formModel) {
      if (this.formModel.judgementCode) {

        this.setSelectedJudgment();
      }
      this.readCurrentUserInfo();
    }

    this.$set(this.formModel, 'appealType', this.formModel.appealType ? this.formModel.appealType : {
      Text: '',
      Value: '',
    });

    if (this.formModel.appealType && this.formModel.appealType.Value) {
      this.formModel.appealType.Value = parseInt(this.formModel.appealType.Value);
      this.currentAppealType = parseInt(this.formModel.appealType.Value);
    }

    this.loading = true;
    const params = {
      isPega: this.formModel.isPega,
      objectKey: this.formModel.case.objectKey,
    };
    this.getPortalTypeOfAppealRequest(this.formModel.case.caseLifeTimeObjectKey).then((res) => {
      this.validAppealTypes = this.getValidAppealTypes(res.data);
      if (this.validAppealTypes.length == 1) {
        this.formModel.appealType.Value = this.validAppealTypes[0].id;
        this.formModel.appealType.Text = this.validAppealTypes[0].name;
      }
    });
    if (this.routeRequestObjectKey || this.requiredData?.isFromNonCaseParty) {

      this.getJudgementsByRequestObjectKey(this.routeRequestObjectKey ? this.routeRequestObjectKey : this.requiredData?.parentRequestObjectKey)
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();

        })
        .finally(() => (this.loading = false));
    }
    else {
      this.getCaseJudgments(params)
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();
        })
        .finally(() => (this.loading = false));
    }

  },
  created() {
  },
  methods: {
    ...mapActions([
      'getCaseJudgments',
      'uploadAttachment',
      'isValidVerdictApplicantForCommercialJudgement',
      'getPortalTypeOfAppealRequest',
      'isJudgementIssuedFromCommercialCourt',
      'getJudgementsByRequestObjectKey',
    ]),
    setSelectedJudgment() {
      this.formModel.judgementCode && this.$set(this, 'selectedJudgment', (this.judgments || []).find(a => a.judgementCode == this.formModel.judgementCode) || {});

      if (this.formModel.judgementCode !== undefined) {
        this.loading = true;
        this.isValidVerdictApplicantForCommercialJudgement(this.formModel.judgementCode)
          .then(() => {
            this.$set(this.formModel, 'isApplicantEligibleToApply', true);
          })
          .catch(() => {
            this.$set(this.formModel, 'isApplicantEligibleToApply', false);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.$set(this.formModel, 'isApplicantEligibleToApply', undefined);
      }
    },
    getJudgementDeed() {
      if (this.selectedJudgment.isSecretJudgements) {
        this.showAlertModal = true;
      } else {
        this.showCaseJudgementModal = true;
      }
    },
    closeCaseJudgementModal() {
      this.showCaseJudgementModal = false;
    },
    fileSelected({ selectedAttachment: attachment, selectedAttachmentKey }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            this.$set(this.formModel, 'judgementFile', [
              {
                fileData: {
                  attachement: data,
                  fileName: attachment.name,
                  fileType: attachment.fileObject.type,
                  sharePointId: data,
                  extension: attachment.extension,
                  fileCategory: attachment.category,
                  attachmentKey: selectedAttachmentKey,
                },
              },
            ]);
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    showDeleteObjectionNoteModal() {
      this.showDeleteObjectionNoteAttachment = true;
    },
    confirmDeleteObjectionNote() {
      this.$delete(this.formModel.judgementFile, 0);
      this.cancelDeleteObjectionNote();
    },
    cancelDeleteObjectionNote() {
      this.showDeleteObjectionNoteAttachment = false;
    },
    viewAttachment() {
      if (this.formModel.judgementFile[0].fileData.hide == true)
        this.showAlertModalForCriminalCases = true;
      else
        this.showViewAttachmentModal = true;
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },
    readCurrentUserInfo() {
      if (this.userInfo) {
        const socialTypeId = this.userInfo.socialIdType;
        this.$set(this.formModel, 'socialIdType', socialTypeId);
        this.$set(this.formModel, 'socialId', this.userInfo.socialId);
        this.$set(this.formModel, 'firstName', this.userInfo.firstName);
        this.$set(this.formModel, 'fatherName', this.userInfo.fatherName);
        this.$set(this.formModel, 'grandFatherName', this.userInfo.grandFatherName);
        this.$set(this.formModel, 'familyName', this.userInfo.familyName);
        this.$set(this.formModel, 'dateOfBirth', this.userInfo.dateOfBirth);
        this.$set(this.formModel, 'nationalityCountryId', socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId);
        this.$set(this.formModel, 'sex', this.userInfo.gender);
        this.$set(this.formModel, 'isRetrievedFromMoi', true);
        this.$set(this.formModel, 'fullName', this.userInfo.fullName);
      } else {
        this.$set(this.formModel, 'socialIdType', null);
        this.$set(this.formModel, 'socialId', null);
        this.$set(this.formModel, 'firstName', null);
        this.$set(this.formModel, 'fatherName', null);
        this.$set(this.formModel, 'grandFatherName', null);
        this.$set(this.formModel, 'familyName', null);
        this.$set(this.formModel, 'dateOfBirth', null);
        this.$set(this.formModel, 'nationalityCountryId', null);
        this.$set(this.formModel, 'sex', null);
        this.$set(this.formModel, 'isRetrievedFromMoi', false);
        this.$set(this.formModel, 'fullName', null);
      }
    },
    getValidAppealTypes(ids) {
      if (ids.length === 0) return this.appealType;
      return this.appealType.filter(item => ids.includes(item.id));
    },
  },
};
</script>

<style lang="scss" scoped>
.judgement-deed {
  display: flex;
  align-items: center;
  justify-content: center;
}
.memorandum-challenge-alert-container {
  .alert-notification {
    display: block;
    color: #f6c645;
    font-size: 14px;
  }
  .message {
    font-size: 16px;
    color: #faad14;
  }
}

.objection-judgment-confirm-text {
  color: darkred;
  font-size: 1rem;
  margin-bottom: 0;
}

.objection-note-attachment {
  display: flex;
  .objection-judgment-confirm-text {
    margin-left: 10px;
  }
  .uplaod-attachments-button .img-actions {
    width: 100% !important;
  }
}

::v-deep .attachment-item {
  cursor: pointer;
}
</style>